import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { formatIDR, TIER_META, TIER_ORDER, TIER_THRESHOLDS } from "@/lib/format";
import { Loading, TierBadge } from "@/components/kit";
import { CalendarDays, Gift, CreditCard, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    api.getMember(user.member_id).then(setMember).catch(() => {});
    api.listPromos().then(setPromos).catch(() => {});
  }, [user.member_id]);

  if (!member) return <Loading />;
  const meta = TIER_META[member.tier] || TIER_META.Bronze;
  const idx = TIER_ORDER.indexOf(member.tier);
  const nextTier = TIER_ORDER[idx + 1];
  const nextThresh = nextTier ? TIER_THRESHOLDS[nextTier] : null;
  const progress = nextThresh ? Math.min(100, (member.lifetime_spend / nextThresh) * 100) : 100;

  return (
    <div className="space-y-6 fade-in">
      <div>
        <div className="hsc-label">Selamat datang</div>
        <h1 className="font-display text-3xl text-white">{member.name}</h1>
      </div>

      <div className="rounded-3xl border p-6" style={{ background: meta.bg, borderColor: meta.border }}>
        <div className="flex items-center justify-between">
          <TierBadge tier={member.tier} />
          <span className="text-[11px] uppercase tracking-widest text-[#8E8E93]">Member sejak {member.member_since}</span>
        </div>
        <div className="mt-6 flex items-end justify-between">
          <div>
            <div className="hsc-label">Poin Anda</div>
            <div className="font-display text-4xl" style={{ color: meta.accent }}>{member.points}</div>
          </div>
          <div className="text-right">
            <div className="hsc-label">Total Belanja</div>
            <div className="font-display text-xl text-white">{formatIDR(member.lifetime_spend)}</div>
          </div>
        </div>
        {nextTier && (
          <div className="mt-5">
            <div className="mb-1.5 flex justify-between text-[11px] text-[#8E8E93]">
              <span>Menuju {nextTier}</span><span>{formatIDR(Math.max(0, nextThresh - member.lifetime_spend))} lagi</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-black/40">
              <div className="h-full rounded-full" style={{ width: `${progress}%`, background: meta.accent }} />
            </div>
          </div>
        )}
      </div>

      {!!promos.length && (
        <div className="flex snap-x gap-3 overflow-x-auto pb-2">
          {promos.map((p) => (
            <img key={p.id} src={fileUrl(p.image_path)} alt={p.title} className="h-40 w-72 shrink-0 snap-center rounded-2xl border border-[#1F1F22] object-cover" />
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {[
          { to: "/member/booking", icon: CalendarDays, label: "Kelas" },
          { to: "/member/rewards", icon: Gift, label: "Rewards" },
          { to: "/member/card", icon: CreditCard, label: "Kartu" },
        ].map((q) => {
          const Icon = q.icon;
          return (
            <button key={q.to} onClick={() => navigate(q.to)} className="flex flex-col items-center gap-2 rounded-2xl border border-[#1F1F22] bg-[#0A0A0C] py-5 text-[12px] text-[#D1D1D6] transition-colors hover:border-[#6E6E73]">
              <Icon className="h-6 w-6" /> {q.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-[#1F1F22] p-4">
        <div className="hsc-label mb-2">{meta.tagline}</div>
        <ul className="space-y-1.5">
          {meta.benefits.map((b, i) => (
            <li key={i} className="flex items-center gap-2 text-[13px] text-[#A1A1A6]"><ChevronRight className="h-3.5 w-3.5 text-[#5A5A5F]" />{b}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
